import React, { useEffect, useMemo, useState } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import {
  METER_BOTH,
  METER_HOUR,
  METER_ODOMETER,
  normalizeMeterType,
} from "./fieldAppMeterLabels";
import {
  MAINTENANCE_ITEM_CATEGORIES,
  SCHEDULE_BY_DATE,
  SCHEDULE_BY_HOURS,
  SCHEDULE_BY_KM,
  SCHEDULE_MODE_DATE,
  SCHEDULE_MODE_METER,
} from "./maintenanceItemCategories";
import {
  addCustomMaintenanceItem,
  loadCustomMaintenanceItems,
} from "./customMaintenanceItems";

function todayIso() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function meterNeeds(meterType) {
  const normalized = normalizeMeterType(meterType);
  return {
    needsHours:
      normalized === METER_HOUR ||
      normalized === METER_BOTH ||
      normalized === "h" ||
      !normalized,
    needsKm:
      normalized === METER_ODOMETER ||
      normalized === METER_BOTH ||
      normalized === "km",
    kmOnly: normalized === METER_ODOMETER || normalized === "km",
  };
}

/** h/km vem do cadastro da máquina — não é escolhido no formulário. */
function scheduleByFromMachine(needs) {
  if (needs.kmOnly) return SCHEDULE_BY_KM;
  return SCHEDULE_BY_HOURS;
}

/**
 * Modal multi-item: marcar itens, registrar feitos agora e/ou agendar próxima
 * (horímetro, km ou data). Itens customizados ficam salvos por fazenda.
 */
export default function FieldAppAddMaintenanceModal({
  farmId,
  machines = [],
  initialEquipmentId = null,
  t,
  saving,
  onClose,
  onSubmit,
}) {
  const initialMachine =
    machines.find(
      (item) => String(item.equipmentId) === String(initialEquipmentId)
    ) ||
    machines[0] ||
    null;

  const [equipmentId, setEquipmentId] = useState(
    initialMachine ? String(initialMachine.equipmentId) : ""
  );
  const [customItems, setCustomItems] = useState(() =>
    loadCustomMaintenanceItems(farmId)
  );
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [newItemName, setNewItemName] = useState("");
  const [registerDoneNow, setRegisterDoneNow] = useState(true);
  const [scheduleNext, setScheduleNext] = useState(true);
  const [whatWasDone, setWhatWasDone] = useState("");
  const [nextNotes, setNextNotes] = useState("");
  const [scheduledDate, setScheduledDate] = useState(todayIso());
  const [targetValue, setTargetValue] = useState("");
  const [scheduleMode, setScheduleMode] = useState(SCHEDULE_MODE_METER);
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialEquipmentId != null) {
      setEquipmentId(String(initialEquipmentId));
    }
  }, [initialEquipmentId]);

  useEffect(() => {
    setCustomItems(loadCustomMaintenanceItems(farmId));
  }, [farmId]);

  const selectedMachine = useMemo(
    () =>
      machines.find((item) => String(item.equipmentId) === String(equipmentId)) ||
      null,
    [equipmentId, machines]
  );

  const needs = useMemo(
    () => meterNeeds(selectedMachine && selectedMachine.meterType),
    [selectedMachine]
  );

  const machineScheduleBy = useMemo(
    () => scheduleByFromMachine(needs),
    [needs]
  );

  const meterUnitLabel = machineScheduleBy === SCHEDULE_BY_KM ? "km" : "h";
  const meterChoiceLabel =
    machineScheduleBy === SCHEDULE_BY_KM
      ? t("Quilometragem (km)")
      : t("Horímetro (h)");

  const allItems = useMemo(() => {
    const preset = MAINTENANCE_ITEM_CATEGORIES.map((item) => item.label);
    const extras = customItems.filter(
      (label) =>
        !preset.some((item) => item.toLowerCase() === label.toLowerCase())
    );
    return [...preset, ...extras];
  }, [customItems]);

  const toggleItem = (label) => {
    setSelectedLabels((current) =>
      current.includes(label)
        ? current.filter((item) => item !== label)
        : [...current, label]
    );
  };

  const handleAddCustomItem = () => {
    const trimmed = (newItemName || "").trim();
    if (!trimmed) {
      setError(t("Informe o nome do novo item."));
      return;
    }
    const updated = addCustomMaintenanceItem(farmId, trimmed);
    setCustomItems(updated);
    setSelectedLabels((current) =>
      current.includes(trimmed) ? current : [...current, trimmed]
    );
    setNewItemName("");
    setError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!equipmentId) {
      setError(t("Selecione a máquina."));
      return;
    }
    if (!selectedLabels.length) {
      setError(t("Marque ao menos um item de manutenção."));
      return;
    }
    if (!registerDoneNow && !scheduleNext) {
      setError(
        t("Marque “feita agora” e/ou “agendar próxima manutenção”.")
      );
      return;
    }
    if (registerDoneNow && !(whatWasDone || "").trim()) {
      setError(t("Descreva o que foi feito agora."));
      return;
    }

    let targetHourMeter;
    let targetKm;
    const resolvedScheduleBy =
      scheduleMode === SCHEDULE_MODE_DATE
        ? SCHEDULE_BY_DATE
        : machineScheduleBy;

    if (scheduleNext) {
      if (resolvedScheduleBy === SCHEDULE_BY_DATE) {
        if (!scheduledDate) {
          setError(t("Informe a data da próxima manutenção."));
          return;
        }
      } else {
        const parsedTarget = Number(String(targetValue).replace(",", "."));
        if (!targetValue || !Number.isFinite(parsedTarget) || parsedTarget < 0) {
          setError(
            resolvedScheduleBy === SCHEDULE_BY_KM
              ? t("Informe a quilometragem da próxima manutenção.")
              : t("Informe o horímetro da próxima manutenção.")
          );
          return;
        }
        if (resolvedScheduleBy === SCHEDULE_BY_KM) {
          targetKm = parsedTarget;
        } else {
          targetHourMeter = parsedTarget;
        }
      }
    }

    setError("");
    onSubmit({
      equipmentId: Number(equipmentId),
      names: selectedLabels,
      registerDoneNow,
      scheduleNext,
      scheduleBy: scheduleNext ? resolvedScheduleBy : undefined,
      whatWasDone: registerDoneNow
        ? (whatWasDone || "").trim() || undefined
        : undefined,
      nextNotes: scheduleNext
        ? (nextNotes || "").trim() || undefined
        : undefined,
      scheduledDate: scheduleNext
        ? scheduledDate || todayIso()
        : todayIso(),
      targetHourMeter,
      targetKm,
    });
  };

  return (
    <div
      className="field-app-modal-backdrop"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="field-app-modal field-app-add-maintenance-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="field-app-add-maintenance-title"
      >
        <div className="field-app-modal-header">
          <h3 id="field-app-add-maintenance-title">
            {t("Adicionar manutenção")}
          </h3>
          <button
            type="button"
            className="close"
            onClick={onClose}
            aria-label={t("Fechar")}
            disabled={saving}
          >
            <FaTimes aria-hidden />
          </button>
        </div>
        <form
          className="field-app-complete-maintenance-form"
          onSubmit={handleSubmit}
        >
          <p className="text-muted" style={{ marginTop: 0 }}>
            {t(
              "Marque um ou mais itens. Depois escolha registrar o que foi feito agora e/ou agendar a próxima (horímetro, km ou data)."
            )}
          </p>

          <label htmlFor="add-maintenance-machine">{t("Máquina")} *</label>
          <select
            id="add-maintenance-machine"
            className="form-control"
            value={equipmentId}
            onChange={(event) => setEquipmentId(event.target.value)}
            required
            disabled={saving || Boolean(initialEquipmentId)}
          >
            {!machines.length ? (
              <option value="">{t("Nenhuma máquina na lista")}</option>
            ) : (
              machines.map((machine) => (
                <option
                  key={machine.equipmentId}
                  value={String(machine.equipmentId)}
                >
                  {machine.equipmentName || machine.equipmentId}
                </option>
              ))
            )}
          </select>

          <fieldset className="field-app-add-maintenance-section">
            <legend>{t("Itens de manutenção")} *</legend>
            <p className="text-muted field-app-add-maintenance-section-hint">
              {t("Marque quantos quiser. Dá para criar um nome novo e salvar na lista.")}
            </p>
            <div
              className="field-app-add-maintenance-checklist"
              role="group"
              aria-label={t("Itens de manutenção")}
            >
              {allItems.map((label) => {
                const checked = selectedLabels.includes(label);
                const inputId = `add-maint-item-${label}`;
                return (
                  <label
                    key={label}
                    htmlFor={inputId}
                    className={`field-app-add-maintenance-check${
                      checked ? " is-checked" : ""
                    }`}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleItem(label)}
                      disabled={saving}
                    />
                    <span>{t(label)}</span>
                  </label>
                );
              })}
            </div>

            <div className="field-app-add-maintenance-new-item">
              <label htmlFor="add-maintenance-new-item">
                {t("Criar novo item")}
              </label>
              <div className="field-app-add-maintenance-new-item-row">
                <input
                  id="add-maintenance-new-item"
                  type="text"
                  className="form-control"
                  value={newItemName}
                  onChange={(event) => setNewItemName(event.target.value)}
                  placeholder={t("Ex.: Correia do alternador")}
                  disabled={saving}
                  maxLength={200}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleAddCustomItem();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={handleAddCustomItem}
                  disabled={saving}
                >
                  <FaPlus aria-hidden /> {t("Salvar na lista")}
                </button>
              </div>
            </div>
          </fieldset>

          <fieldset className="field-app-add-maintenance-section">
            <legend>{t("O que registrar")} *</legend>
            <label className="field-app-add-maintenance-check is-action">
              <input
                type="checkbox"
                checked={registerDoneNow}
                onChange={(event) => setRegisterDoneNow(event.target.checked)}
                disabled={saving}
              />
              <span>{t("Manutenção feita agora")}</span>
            </label>
            <label className="field-app-add-maintenance-check is-action">
              <input
                type="checkbox"
                checked={scheduleNext}
                onChange={(event) => setScheduleNext(event.target.checked)}
                disabled={saving}
              />
              <span>{t("Agendar próxima manutenção")}</span>
            </label>
          </fieldset>

          {registerDoneNow ? (
            <fieldset className="field-app-add-maintenance-section">
              <legend>{t("Feita agora")}</legend>
              <label htmlFor="add-maintenance-done">
                {t("Descrição do serviço realizado")} *
              </label>
              <textarea
                id="add-maintenance-done"
                className="form-control"
                rows={3}
                value={whatWasDone}
                onChange={(event) => setWhatWasDone(event.target.value)}
                placeholder={t(
                  "Ex.: Óleo Shell 15W40, filtro Donaldson; pastilhas dianteiras"
                )}
                disabled={saving}
                maxLength={2000}
              />
            </fieldset>
          ) : null}

          {scheduleNext ? (
            <fieldset className="field-app-add-maintenance-section">
              <legend>{t("Próxima manutenção")}</legend>
              <p className="text-muted field-app-add-maintenance-section-hint">
                {t(
                  "Escolha data ou a leitura da máquina. Horímetro ou quilometragem vem do cadastro do veículo."
                )}
              </p>
              <div
                className="field-app-add-maintenance-schedule-by"
                role="radiogroup"
                aria-label={t("Tipo de previsão")}
              >
                <label
                  className={`field-app-add-maintenance-check is-action${
                    scheduleMode === SCHEDULE_MODE_METER ? " is-checked" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="schedule-mode"
                    value={SCHEDULE_MODE_METER}
                    checked={scheduleMode === SCHEDULE_MODE_METER}
                    onChange={() => setScheduleMode(SCHEDULE_MODE_METER)}
                    disabled={saving}
                  />
                  <span>{meterChoiceLabel}</span>
                </label>
                <label
                  className={`field-app-add-maintenance-check is-action${
                    scheduleMode === SCHEDULE_MODE_DATE ? " is-checked" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="schedule-mode"
                    value={SCHEDULE_MODE_DATE}
                    checked={scheduleMode === SCHEDULE_MODE_DATE}
                    onChange={() => setScheduleMode(SCHEDULE_MODE_DATE)}
                    disabled={saving}
                  />
                  <span>{t("Data")}</span>
                </label>
              </div>

              {scheduleMode === SCHEDULE_MODE_DATE ? (
                <>
                  <label htmlFor="add-maintenance-date">
                    {t("Data da próxima")} *
                  </label>
                  <input
                    id="add-maintenance-date"
                    type="date"
                    className="form-control"
                    value={scheduledDate}
                    onChange={(event) => setScheduledDate(event.target.value)}
                    disabled={saving}
                    required
                  />
                </>
              ) : (
                <>
                  <label htmlFor="add-maintenance-target">
                    {t("Próxima manutenção com ({{unit}})", {
                      unit: meterUnitLabel,
                    })}{" "}
                    *
                  </label>
                  <input
                    id="add-maintenance-target"
                    type="number"
                    className="form-control"
                    value={targetValue}
                    onChange={(event) => setTargetValue(event.target.value)}
                    min="0"
                    step="any"
                    required
                    disabled={saving}
                  />
                  <p className="text-muted field-app-add-maintenance-section-hint">
                    {machineScheduleBy === SCHEDULE_BY_KM
                      ? t(
                          "Unidade definida no cadastro desta máquina: quilometragem."
                        )
                      : t(
                          "Unidade definida no cadastro desta máquina: horímetro."
                        )}
                  </p>
                </>
              )}

              <label htmlFor="add-maintenance-next-notes">
                {t("Observação da próxima (opcional)")}
              </label>
              <textarea
                id="add-maintenance-next-notes"
                className="form-control"
                rows={2}
                value={nextNotes}
                onChange={(event) => setNextNotes(event.target.value)}
                placeholder={t(
                  "Ex.: Usar o mesmo óleo; verificar vazamento no radiador"
                )}
                disabled={saving}
                maxLength={2000}
              />
            </fieldset>
          ) : null}

          {error ? (
            <p className="text-danger" role="alert" style={{ marginTop: 12 }}>
              {error}
            </p>
          ) : null}

          <div className="field-app-complete-maintenance-actions">
            <button
              type="button"
              className="btn btn-default"
              onClick={onClose}
              disabled={saving}
            >
              {t("Cancelar")}
            </button>
            <button
              type="submit"
              className="btn btn-success"
              disabled={saving || !machines.length}
            >
              {saving ? t("Salvando…") : t("Salvar manutenções")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
